import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

// Define interfaces
interface PolicyReviewHistory {
  reviewedAt: any;
  reviewedBy: string;
  previewVersion?: string;
  previousVersion?: string;
}

interface PolicyHistoryDialogProps {
  history: PolicyReviewHistory[];
  isOpen: boolean;
  onClose: () => void;
  userName?: (userId: string) => Promise<string>;
  onRevertReview?: (review: PolicyReviewHistory) => void;
}

const PolicyHistoryDialog = ({ history, isOpen, onClose, userName, onRevertReview }: PolicyHistoryDialogProps) => {
  const [userNames, setUserNames] = useState({} as Record<string, string>);

  // Fetch user names for all reviewers if userName function is provided
  const loadUserNames = async () => {
    if (!userName || history.length === 0) return;

    const names: Record<string, string> = {};

    for (const review of history) {
      if (review.reviewedBy && !userNames[review.reviewedBy]) {
        try {
          const name = await userName(review.reviewedBy);
          names[review.reviewedBy] = name;
        } catch (error) {
          console.error('Error fetching user name:', error);
          names[review.reviewedBy] = 'Unknown User';
        }
      }
    }

    setUserNames((prev) => ({ ...prev, ...names }));
  };

  // Format date
  const formatDate = (date: any) => {
    if (!date) return 'N/A';

    try {
      if (date.toDate) {
        return format(date.toDate(), 'PPP');
      }
      return format(new Date(date), 'PPP');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Load user names when history changes
  useEffect(() => {
    if (userName && history.length > 0) {
      loadUserNames();
    }
  }, [history]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 p-4 py-6 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-lg font-medium">Policy Review History</DialogTitle>
          </div>

          <div className="py-2 pl-4 overflow-hidden">
            {history.length > 0 ? (
              <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto pr-2">
                {history.map((review, index) => (
                  <div key={index} className="py-3">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-sm font-medium">
                        Review on {formatDate(review.reviewedAt)}
                      </h3>
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {review.previewVersion || review.previousVersion}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Reviewed by {userNames[review.reviewedBy] || 'Unknown User'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No review history available for this policy.
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <Button variant="outline" onClick={onClose} className="w-full text-center justify-center">
              Close
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyHistoryDialog;
